import { createChatCompletion } from './openai'
//import ReactMarkdown from 'react-markdown'
import { ChatErrors } from '../chat/errorHandling'
// Import auth hooks for token
import { useAuth } from '../firebase/auth'

// Helper function to create a delay
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

// System message to set the AI's behavior and context
const systemMessage = {
  role: 'system',
  content: `You are KAI, an AI assistant for KET Academy. Your purpose is to provide comprehensive support to students, educators, and visitors, empowering them with knowledge and guidance about KET Academy and its offerings. You are the first point of contact for new visitors and a mentor for students and educators.

Your responses should always be:
- **Helpful and educational**: Provide accurate, reliable, and relevant information about academic topics, KET Academy's services, and its mission.
- **Clear and concise**: Communicate information in an easy-to-understand manner, avoiding unnecessary complexity or jargon.
- **Friendly and encouraging**: Create a welcoming atmosphere, whether answering academic questions or introducing KET Academy to a new visitor.
- **Comprehensive yet focused**: Adapt your responses to the user's needs, whether they're seeking academic support, exploring KET Academy, or learning how to engage with its resources.
- **Professional yet approachable**: Maintain a warm, empathetic tone while upholding professionalism in all interactions.

Additionally:
- Use **markdown formatting** when appropriate to enhance readability (e.g., headings, bullet points, code blocks).
- Provide **detailed explanations**, **practical examples**, or **step-by-step guidance** to help users understand academic concepts or navigate the website.
- Offer **personalized recommendations**, such as KET Academy's services, study tips, or upcoming events, based on user interests and queries.
- Be proactive in addressing common questions about KET Academy, including its mission, vision, services, and how to get involved.
- Always be **respectful, patient, and supportive**, ensuring a positive and informative experience for all users.

### Key Roles:
1. **Academic Support**: Mentor students by providing clear explanations, resources, and motivation to help them succeed academically.
2. **Visitor Guide**: Assist new visitors in understanding KET Academy's mission, vision, services, and unique value. Help them navigate the website and discover relevant sections like courses, mentorship opportunities, and partnerships.
3. **Community Builder**: Encourage engagement with KET Academy by explaining how users can contribute, participate in programs, or become ambassadors.
4. **Technical Assistant**: Offer troubleshooting guidance for any technical issues users may encounter while using KET Academy's platform.

Remember, your role is to inspire, inform, and engage every user, reflecting KET Academy's commitment to bridging educational gaps, fostering community, and empowering students with the tools for success.`,
}

/**
 * Generate an AI response using the secure OpenAI API proxy
 *
 * This function has been updated to always use the secure backend proxy,
 * eliminating any direct browser-to-OpenAI communication.
 *
 * @param {string} userInput - The user's message
 * @param {function} onStream - Callback for streaming responses
 * @param {string} systemPrompt - Optional custom system prompt
 * @param {Array} conversationHistory - Previous messages
 * @param {Object} knowledgeBase - Optional knowledge base data
 * @returns {Promise<string>} - The complete AI response
 */
export const generateAIResponse = async (
  userInput,
  onStream,
  systemPrompt = '',
  conversationHistory = [],
  knowledgeBase = null,
  authToken = null
) => {
  try {
    // Include knowledge base in system prompt if available
    const enhancedSystemPrompt = knowledgeBase
      ? `${systemPrompt}\n\nAvailable Knowledge Base:\n${JSON.stringify(
          knowledgeBase,
          null,
          2
        )}`
      : systemPrompt

    const messages = [
      {
        role: 'system',
        content: enhancedSystemPrompt || systemMessage.content,
      },
      ...conversationHistory.map((msg) => ({
        role: msg.type === 'user' ? 'user' : 'assistant',
        content: msg.content,
      })),
      {
        role: 'user',
        content: userInput,
      },
    ]

    // Get a streaming response through our secure proxy
    const response = await createChatCompletion(messages, {
      model: 'gpt-3.5-turbo',
      temperature: 0.7,
      max_tokens: 1000,
      stream: true,
      authToken: authToken,
    })

    // Handle streaming
    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let fullResponse = ''

    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      const chunk = decoder.decode(value)

      // OpenAI uses SSE (Server-Sent Events) format
      // We need to parse the data lines
      const lines = chunk
        .split('\n')
        .filter((line) => line.trim() !== '' && line.trim() !== 'data: [DONE]')

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const jsonString = line.slice(6) // Remove 'data: '
            const jsonData = JSON.parse(jsonString)
            const content = jsonData.choices?.[0]?.delta?.content || ''

            if (content) {
              fullResponse += content

              // Stream with a slight delay for natural appearance
              await delay(10)
              onStream(content)
            }
          } catch (e) {
            // Skip invalid JSON lines
            console.error('Error parsing SSE data:', e)
          }
        }
      }
    }

    return fullResponse
  } catch (error) {
    console.error('OpenAI API Error:', {
      message: error.message,
      type: error.type,
      code: error.code,
    })

    // Map errors to our custom ChatErrors
    if (error.message?.includes('rate limit')) {
      throw new Error(ChatErrors.RATE_LIMIT)
    } else if (
      error.message?.includes('auth') ||
      error.message?.includes('key')
    ) {
      throw new Error(ChatErrors.AUTHENTICATION_ERROR)
    } else if (error.message?.includes('invalid')) {
      throw new Error(ChatErrors.VALIDATION_ERROR)
    } else if (error.message?.includes('network')) {
      throw new Error(ChatErrors.NETWORK_ERROR)
    } else {
      throw new Error(ChatErrors.API_ERROR)
    }
  }
}

// Auth-aware hook for generating AI responses
export const useAIResponse = () => {
  const { authUser } = useAuth()

  return {
    generateResponse: (
      userInput,
      onStream,
      systemPrompt,
      conversationHistory,
      knowledgeBase
    ) => {
      // Get the auth token if the user is authenticated
      const getIdToken = async () => {
        if (!authUser) return null
        try {
          return await authUser.getIdToken()
        } catch (e) {
          console.error('Failed to get auth token:', e)
          return null
        }
      }

      // Generate response with auth token
      return getIdToken().then((token) => {
        return generateAIResponse(
          userInput,
          onStream,
          systemPrompt,
          conversationHistory,
          knowledgeBase,
          token
        )
      })
    },
  }
}
